import React, { useEffect, useRef, useState } from "react";
import { Paperclip, Upload, Download, Trash2, Send, MessageSquare, Loader2, FileText, Shield, User } from "lucide-react";
import { documentsApi, commentsApi, auth } from "../lib/api";

const fmt = (iso) => { try { return new Date(iso).toLocaleString("en-IN"); } catch { return iso; } };
const bytes = (n) => n < 1024 ? `${n} B` : n < 1024*1024 ? `${(n/1024).toFixed(1)} KB` : `${(n/1024/1024).toFixed(1)} MB`;

const ProjectDetail = ({ projectId }) => {
  const me = auth.getUser();
  const [docs, setDocs] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const [label, setLabel] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const listRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([documentsApi.list(projectId), commentsApi.list(projectId)]);
      setDocs(d);
      setComments(c);
    } catch (e) {
      setErr("Failed to load project data");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [comments.length]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr("");
    setUploading(true);
    try {
      await documentsApi.upload(projectId, file, label);
      setLabel("");
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const delDoc = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await documentsApi.remove(id);
    load();
  };

  const download = async (doc) => {
    try { await documentsApi.download(doc.id, doc.filename); }
    catch (e) { setErr("Download failed"); }
  };

  const post = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    try {
      await commentsApi.create(projectId, body.trim());
      setBody("");
      const c = await commentsApi.list(projectId);
      setComments(c);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed to post");
    } finally {
      setSending(false);
    }
  };

  const delComment = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    await commentsApi.remove(id);
    const c = await commentsApi.list(projectId);
    setComments(c);
  };

  if (loading) return <div className="py-6 flex items-center text-sm text-slate-500"><Loader2 className="animate-spin mr-2"/> Loading...</div>;

  return (
    <div className="mt-4 grid lg:grid-cols-2 gap-5">
      {/* Documents */}
      <div className="bg-slate-50 border border-slate-100 rounded-xl p-4">
        <div className="flex items-center gap-2 font-semibold text-slate-900"><Paperclip size={16} className="text-brand"/> Documents ({docs.length})</div>
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} className="contact-input text-sm" placeholder="Label (e.g. Gap Analysis Report)" />
          <label className="btn-outline text-sm cursor-pointer inline-flex justify-center whitespace-nowrap">
            <Upload size={14}/> {uploading ? "Uploading..." : "Upload file"}
            <input ref={fileRef} type="file" onChange={onUpload} disabled={uploading} className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt,.csv" />
          </label>
        </div>
        <p className="text-[11px] text-slate-500 mt-1">Allowed: PDF, DOC, XLS, PNG, JPG, TXT, CSV  Max 10 MB</p>
        {err && <div className="mt-2 text-xs text-red-600">{err}</div>}

        <ul className="mt-4 space-y-2">
          {docs.length === 0 && <li className="text-sm text-slate-500">No documents uploaded yet.</li>}
          {docs.map((d) => (
            <li key={d.id} className="bg-white border border-slate-100 rounded-lg p-3 flex items-center justify-between gap-3">
              <div className="min-w-0 flex items-start gap-3">
                <FileText size={18} className="text-brand flex-shrink-0 mt-0.5"/>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{d.filename}</div>
                  {d.label && <div className="text-xs text-slate-500">{d.label}</div>}
                  <div className="text-[11px] text-slate-400">{bytes(d.size)}  by {d.uploaded_by_name || d.uploaded_by} ({d.uploaded_by_role})  {fmt(d.created_at)}</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => download(d)} className="p-2 text-brand hover:bg-red-50 rounded-md" title="Download"><Download size={16}/></button>
                {(me?.role === "admin" || d.uploaded_by === me?.email) && (
                  <button onClick={() => delDoc(d.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-md" title="Delete"><Trash2 size={16}/></button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Comments */}
      <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 flex flex-col">
        <div className="flex items-center gap-2 font-semibold text-slate-900"><MessageSquare size={16} className="text-brand"/> Project Discussion ({comments.length})</div>
        <div ref={listRef} className="mt-3 flex-1 space-y-3 max-h-[420px] overflow-y-auto pr-1">
          {comments.length === 0 && <div className="text-sm text-slate-500">No messages yet. Start the conversation below.</div>}
          {comments.map((c) => {
            const isMine = c.author_email === me?.email;
            const isAdmin = c.author_role === "admin";
            return (
              <div key={c.id} className={`flex gap-2 ${isMine ? "justify-end" : "justify-start"}`}>
                {!isMine && (
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isAdmin ? "bg-brand text-white" : "bg-slate-200 text-slate-700"}`}>
                    {isAdmin ? <Shield size={14}/> : <User size={14}/>}
                  </div>
                )}
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 border ${isMine ? "bg-brand text-white border-brand rounded-br-sm" : "bg-white border-slate-100 rounded-bl-sm"}`}>
                  <div className={`text-[11px] font-semibold ${isMine ? "text-white/80" : "text-slate-500"}`}>
                    {c.author_name || c.author_email} {isAdmin ? " LIS Team" : ""}
                  </div>
                  <div className={`text-sm whitespace-pre-line mt-0.5 ${isMine ? "text-white" : "text-slate-800"}`}>{c.body}</div>
                  <div className={`text-[10px] mt-1 ${isMine ? "text-white/60" : "text-slate-400"}`}>{fmt(c.created_at)}</div>
                </div>
                {isMine && (me?.role === "admin" || c.author_email === me?.email) && (
                  <button onClick={() => delComment(c.id)} className="p-1 self-center text-red-500 hover:bg-red-50 rounded" title="Delete"><Trash2 size={13}/></button>
                )}
              </div>
            );
          })}
        </div>
        <form onSubmit={post} className="mt-3 flex items-center gap-2">
          <input value={body} onChange={(e) => setBody(e.target.value)} className="contact-input text-sm flex-1" placeholder="Type a message..." />
          <button type="submit" disabled={sending || !body.trim()} className="btn-brand text-sm disabled:opacity-60"><Send size={14}/></button>
        </form>
      </div>
    </div>
  );
};

export default ProjectDetail;
